import { LinearProgress, Stack } from "@mui/material";
import { Box } from "@mui/system";
import { AxiosResponse } from "axios";
import { useCallback } from "react";

import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useModalOpenStatus } from "@/common/hooks/modal/useModalOpenStatus";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import { RepairWorkDto } from "@/core/api/generated/v1";

import ConfirmationModal, { ConfirmationModalProps } from "../../Modals/ConfirmationModal";
import BlockRepairSpec from "../RepairSpec/BlockRepairSpec";

export interface OwnProps {
  repairWork: RepairWorkDto;
  onDelete?: () => void;
  deleteFunc?: (params: { repairWorkId: string }) => Promise<AxiosResponse<unknown, unknown>>;
}

type Props = OwnProps & ConfirmationModalProps;

export default function DeleteRepairWorkModal({
  repairWork,
  onDelete,
  deleteFunc,
  ...dialogProps
}: Props) {
  const { enqueueSnackbar } = useAppSnackbar();
  const modalStatus = useModalOpenStatus(dialogProps);

  const deleteInfoRequest = useApiRequest(
    apiClient.repairWorkApi.apiV1RepairWorkRepairWorkIdDeleteInfoGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      repairWorkId: repairWork.id!,
    },
    {
      skip: !modalStatus.isOpening && !modalStatus.isOpen,
    },
  );

  const handleDeleteConfirmed = useCallback(async () => {
    try {
      deleteFunc
        ? deleteFunc({
            repairWorkId: repairWork.id!,
          })
        : await apiClient.repairWorkApi.apiV1RepairWorkRepairWorkIdDelete({
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            repairWorkId: repairWork.id!,
          });
      enqueueSnackbar("Work was successfully deleted.", { variant: "success" });
      onDelete ? onDelete() : dialogProps?.onClose && dialogProps.onClose({}, "escapeKeyDown");
    } catch (err) {
      const validation2 = ValidationHelper.handleApiErrorResponse(err);
      validation2.hasErrors &&
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
    }
  }, [repairWork, onDelete]);

  return (
    <ConfirmationModal
      title='Delete the repair work?'
      body={
        <Stack spacing={2}>
          <Box>
            {`You're going to delete the repair work`} <strong>{repairWork.name}</strong>.
          </Box>

          {deleteInfoRequest.isLoading && <LinearProgress />}

          {deleteInfoRequest.data &&
            !deleteInfoRequest.isLoading &&
            (deleteInfoRequest.data.affectedRepairSpecs?.length || 0) !== 0 && (
              <Stack spacing={2}>
                <Box>
                  Repair work <strong>{repairWork.name}</strong> is referenced in{" "}
                  <strong>{deleteInfoRequest.data.affectedRepairSpecs?.length}</strong> repair
                  specs. If you confirm, the work will be automatically deleted from the repair
                  specs.
                </Box>
                <Stack spacing={1}>
                  {deleteInfoRequest.data.affectedRepairSpecs?.map((repairSpec, i) => (
                    <BlockRepairSpec key={i} repairSpec={repairSpec} withDetailsToggle withLink />
                  ))}
                </Stack>
              </Stack>
            )}

          <Box>{`This action can't be undone.`}</Box>
        </Stack>
      }
      onCancel={() => dialogProps?.onClose && dialogProps.onClose({}, "escapeKeyDown")}
      onConfirm={handleDeleteConfirmed}
      {...dialogProps}
    />
  );
}
